import { temporaryHashId } from 'common/utils/temporaryHashId';
import { makeCondition } from './utils';

const initialState = {
  id: undefined,
  name: '',
  pages: [],
  translations: {},
  features: {},
  marketing_enabled: null,
};

export const surveyConfigurationState = (state = initialState, action) => {
  switch (action.type) {
    case 'SET_SURVEY': {
      const {
        payload: { survey, features },
      } = action;

      return {
        ...state,
        ...survey,
        features: {
          ...state.features,
          ...features,
        },
      };
    }

    case 'UPDATE_SURVEY_CONFIGURATION': {
      const { keyName, newValue } = action;
      return {
        ...state,
        [keyName]: newValue,
      };
    }

    case 'UPDATE_SURVEY_TRANSLATIONS': {
      const { keyName, newValue } = action;
      const [language, translationKey] = keyName.split('.');

      return {
        ...state,
        translations: {
          ...state.translations,
          [language]: {
            ...state.translations[language],
            [translationKey]: newValue,
          },
        },
      };
    }

    case 'ADD_NEW_QUESTION': {
      const { newSurveyQuestion } = action;
      const newPage = {
        questions: [
          {
            ...newSurveyQuestion,
            page: state.pages.length,
            position: 0,
          },
        ],
      };

      return {
        ...state,
        pages: [...state.pages, newPage],
      };
    }

    case 'UPDATE_QUESTION_ATTRIBUTE': {
      const { questionId, keyName, newValue } = action;

      const newSurveyPages = state.pages.map(page => {
        const newQuestions = page.questions.map(question => {
          const questionToModify = question.id === questionId;

          return questionToModify ? { ...question, [keyName]: newValue } : { ...question };
        });
        return { ...page, questions: newQuestions };
      });

      return { ...state, pages: newSurveyPages };
    }

    case 'UPDATE_QUESTION_TRANSLATION': {
      const { questionId, keyName, newValue } = action;
      const [language, translationKey] = keyName.split('.');

      const newSurveyPages = state.pages.map(page => {
        const newQuestions = page.questions.map(question => {
          const questionToModify = question.id === questionId;

          return questionToModify ?
              {
                ...question,
                translations: {
                  ...question.translations,
                  [language]: {
                    ...question.translations[language],
                    [translationKey]: newValue,
                  },
                },
              }
            : question;
        });
        return { ...page, questions: newQuestions };
      });

      return { ...state, pages: newSurveyPages };
    }

    case 'UPDATE_QUESTION_METADATA': {
      const { questionId, keyName, newValue } = action;

      const newSurveyPages = state.pages.map(page => {
        const newQuestions = page.questions.map(question => {
          const questionToModify = question.id === questionId;

          return questionToModify ?
              {
                ...question,
                metadata: {
                  ...question.metadata,
                  [keyName]: newValue,
                },
              }
            : question;
        });
        return { ...page, questions: newQuestions };
      });

      return { ...state, pages: newSurveyPages };
    }

    case 'ADD_QUESTION_OPTION': {
      const { question } = action;
      const optionNumber = question.options.length + 1;

      const newOption = {
        id: `new-option-${temporaryHashId()}`,
        identifier: `option-${optionNumber}`,
        active: true,
        position: question.options.length,
        integer_value: undefined,
        metadata: {},
        translations: { en: { label: `Option ${optionNumber}` } },
      };

      const newSurveyPages = state.pages.map(page => {
        const newQuestions = page.questions.map(q => {
          const questionToModify = q.id === question.id;

          return questionToModify ?
              {
                ...q,
                options: [...q.options, newOption],
              }
            : q;
        });
        return { ...page, questions: newQuestions };
      });

      return { ...state, pages: newSurveyPages };
    }

    case 'UPDATE_QUESTION_OPTION_SCALAR_MIN_MAX': {
      const { questionId, keyName, newValue } = action;

      const newSurveyPages = state.pages.map(page => {
        const newQuestions = page.questions.map(question => {
          const questionToModify = question.id === questionId;

          const min = keyName === 'min' ? Number(newValue) : question.options[0].integer_value;
          const max =
            keyName === 'max' ?
              Number(newValue)
            : question.options[question.options.length - 1].integer_value;
          const requiredDifference = max - min;
          const requiredTotal = requiredDifference + 1;
          return questionToModify ?
              {
                ...question,
                options: [...Array(requiredTotal).keys()].map(i => ({
                  id: `new-option-${temporaryHashId()}`,
                  active: true,
                  position: i,
                  integer_value: min + i,
                  metadata: {},
                  identifier: (min + i).toString(),
                  translations: {
                    en: {
                      label: `${min + i}`,
                    },
                  },
                })),
              }
            : question;
        });
        return { ...page, questions: newQuestions };
      });

      return { ...state, pages: newSurveyPages };
    }

    case 'UPDATE_QUESTION_OPTION_TRANSLATION': {
      const { optionId, keyName, newValue } = action;

      const [_optionId, language, translationKey] = keyName.split('.');

      const newSurveyPages = state.pages.map(page => {
        const newQuestions = page.questions.map(question => {
          const newOptions = question.options.map(option => {
            const optionToModify = option.id === optionId;

            return optionToModify ?
                {
                  ...option,
                  translations: {
                    ...option.translations,
                    [language]: {
                      ...option.translations[language],
                      [translationKey]: newValue,
                    },
                  },
                }
              : option;
          });
          return { ...question, options: newOptions };
        });
        return { ...page, questions: newQuestions };
      });

      return { ...state, pages: newSurveyPages };
    }

    case 'REMOVE_SURVEY_QUESTION': {
      const { questionId } = action;

      const newPages = state.pages
        .map(page => ({
          ...page,
          questions: page.questions.filter(question => question.id !== questionId),
        }))
        .filter(page => page.questions.length)
        .map((page, pageNumber) => ({
          ...page,
          questions: page.questions.map(question => ({
            ...question,
            page: pageNumber,
          })),
        }));

      return { ...state, pages: newPages };
    }

    case 'REMOVE_QUESTION_OPTION': {
      const { optionId } = action;

      const newSurveyPages = state.pages.map(page => ({
        ...page,
        questions: page.questions.map(question => ({
          ...question,
          options: question.options.filter(option => option.id !== optionId),
        })),
      }));

      return { ...state, pages: newSurveyPages };
    }

    case 'MOVE_QUESTION': {
      const { questionId, destinationQuestionId } = action;

      // Build the new pages immutably. The previous implementation mutated the shared
      // `questions` arrays in place (splice/push), so back-to-back dispatches (e.g. the
      // areas/rewards move fires one MOVE_QUESTION per question in a forEach) could drop
      // the second move nondeterministically. Cloning at every step keeps each dispatch pure.
      const sourcePageIdx = state.pages.findIndex(p => p.questions.some(q => q.id === questionId));
      const destPageIdx = state.pages.findIndex(p =>
        p.questions.some(q => q.id === destinationQuestionId)
      );
      const movedQuestion = state.pages[sourcePageIdx].questions.find(q => q.id === questionId);

      // Remove the moved question from its source page.
      const pagesWithoutMoved = state.pages.map((page, pIdx) =>
        pIdx === sourcePageIdx ?
          { ...page, questions: page.questions.filter(q => q.id !== questionId) }
        : page
      );

      const destinationLeadQuestion = pagesWithoutMoved[destPageIdx].questions[0];

      // Append the moved question to its destination page.
      const newSurveyPages = pagesWithoutMoved.map((page, pIdx) =>
        pIdx === destPageIdx ?
          {
            ...page,
            questions: [
              ...page.questions,
              {
                ...movedQuestion,
                page: destPageIdx,
                position: page.questions.length,
                condition: makeCondition(movedQuestion, destinationLeadQuestion),
              },
            ],
          }
        : page
      );

      return {
        ...state,
        pages: newSurveyPages.filter(page => page.questions.length > 0),
      };
    }

    case 'REORDER_PAGES': {
      const { sourceIndex, destinationIndex } = action;

      const newSurveyPages = [...state.pages];
      const [removed] = newSurveyPages.splice(sourceIndex, 1);
      const reorderedPage = { ...removed, reordered: true };
      newSurveyPages.splice(destinationIndex, 0, reorderedPage);

      return {
        ...state,
        pages: newSurveyPages.map(({ questions, reordered }, pageIdx) => ({
          questions: questions.map((question, questionIdx) => ({
            ...question,
            page: pageIdx,
            position: questionIdx,
            reordered: question.reordered ?? reordered,
          })),
        })),
      };
    }

    case 'RESET_STATE': {
      return initialState;
    }

    default:
      return state;
  }
};

export default surveyConfigurationState;

export const fetchURL = (dispatch, url, options = {}) => {
  dispatch({ type: 'inFlightRequests/INCREMENT' });

  return fetch(url, { credentials: 'include', ...options })
    .then(response => {
      dispatch({ type: 'inFlightRequests/DECREMENT' });
      return response;
    })
    .then(r => {
      if (!r.ok) {
        throw r;
      }
      return r;
    })
    .then(response => response.json());
};

export const setSurveyConfigurationBeingUpdated = loading => dispatch => {
  dispatch({
    type: 'previewOptionsState/SET_REQUEST_STATUS',
    newStatus: { loading },
  });
};

// Function for fetching survey configuration
const fetchSurveyConfigurationData = uuid => dispatch =>
  fetchURL(dispatch, `/admin/company/survey_configurations/${uuid}`);

// Function for updating the state
export const syncStateAfterConfigFetch = response => (dispatch, getState) => {
  const { currentPerson } = getState();

  dispatch({
    type: 'previewOptionsState/SET_UPDATED_AT',
    updatedAt: response.updatedAt,
  });
  dispatch({
    type: 'SET_SURVEY',
    payload: {
      survey: response.configuration,
      features: response.features,
    },
  });
  dispatch({
    type: 'SET_PREVIEW_OPTIONS_AND_DEFAULTS',
    filterOptions: response.filterOptions,
    configurationHash: response.configurationHash,
    publishedHash: response.publishedHash,
    publishedConfiguration: response.publishedConfiguration,
    globalMarketingSettings: response.globalMarketingSettings,
    availableQuestions: response.availableQuestions,
    defaultLanguage: currentPerson.company.surveyDefaultLanguage,
  });
};

// Main function that combines fetching and updating fetchSurveyConfiguration
export const fetchSurveyConfiguration = uuid => dispatch =>
  dispatch(fetchSurveyConfigurationData(uuid)).then(response => {
    if (uuid === 'new') return response;

    dispatch(syncStateAfterConfigFetch(response));
    return response;
  });

export const syncStateAfterConfigUpdate = response => dispatch => {
  dispatch({
    type: 'previewOptionsState/SET_UPDATED_AT',
    updatedAt: response.updatedAt,
  });
  dispatch({
    type: 'UPDATE_SURVEY_CONFIG_HASHES',
    configurationHash: response.configurationHash,
    publishedHash: response.publishedHash,
  });
};
